/**
 * NVR 용량 기준 구역 자동 생성 + CCTV 이름 순차 부여 (순수 함수).
 *
 * 절차:
 *  1. 구역 미배정 카메라만 모아, 가장자리(전체 중심에서 가장 먼 카메라)를 씨앗으로
 *     잡고 가까운 순서대로 담는다. 다음 카메라를 담으면 NVR 용량(또는 채널 상한,
 *     구역 수를 지정했으면 균등 몫)을 넘는 순간 멈춘다. 남은 카메라로 반복.
 *  2. 묶인 카메라들의 무게중심을 원 중심, 가장 먼 멤버까지의 거리를 반지름으로 해
 *     원을 만들고 다각형으로 근사해 zone 폴리곤으로 넘긴다.
 *  3. 구역이 정해진 뒤(신규 + 기존) 이름 없는 카메라에 `{접두어}_{구역}_{순번}` 을
 *     부여한다. 순번은 그 구역에 이미 있는 최대 순번 다음부터.
 *
 * 씨앗을 가장자리에서 뽑는 이유: 중앙에서 시작하면 마지막에 사방에 흩어진
 * 자투리만 남아 반지름이 맵 전체만큼 커지는 구역이 생긴다.
 *
 * 원끼리 겹칠 수 있다(사용자 결정). 구역 소속의 정답은 항상 memberIds 이며
 * 폴리곤 포함 판정으로 다시 계산하지 않는다.
 *
 * JointJS 비의존 — 테스트에서 평범한 데이터로 주입한다.
 */

import { CCTV_NAME_RE } from '../../zone-auto-import'
import type {
  CapacityInput,
  CapacityPlan,
  CctvKind,
  PlannedCapacityZone,
  PlannedCctvName,
  PlannerCamera,
  Pt,
} from '../model/types'

/** 원을 다각형으로 근사할 때의 변 수. */
export const CIRCLE_SEGMENTS = 32

/** `P1_B1_2_6` → 접두어 `P1_B1` / 구역 `2` / 순번 `6`. */
const NAME_PARTS_RE = /^(P\d+_B\d+)_(\d+)_(\d+)$/

/**
 * 폴리곤을 원보다 이만큼(에디터 단위) 더 키운다. 외접 반지름만 쓰면 변의 중앙
 * 방향에 있는 반지름 위의 점이 변에 정확히 얹히고, 경계 위 점의 ray-casting
 * 포함 판정은 불안정하다(zoneCctvRelations.pointInPolygon 주석).
 */
const BOUNDARY_EPSILON = 1e-3

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function centroid(points: Pt[]): Pt {
  let sx = 0
  let sy = 0
  for (const p of points) {
    sx += p.x
    sy += p.y
  }
  return { x: sx / points.length, y: sy / points.length }
}

/** 카메라 1대가 차지하는 용량 (GB). */
export function cameraGb(kind: CctvKind, input: CapacityInput): number {
  return kind === 'omni' ? input.omniGb : input.onewayGb
}

/**
 * 원의 다각형 근사. 내접 다각형은 변이 원보다 안쪽으로 들어가 반지름
 * 위치의 멤버가 폴리곤 밖으로 빠지므로, 외접 반지름(r / cos(π/n))으로 키워
 * 원 안의 모든 점이 폴리곤 안에 들어가게 한다.
 */
export function circlePoints(center: Pt, radius: number, segments = CIRCLE_SEGMENTS): Pt[] {
  const r = radius / Math.cos(Math.PI / segments) + BOUNDARY_EPSILON
  return Array.from({ length: segments }, (_, i) => {
    const t = (2 * Math.PI * i) / segments
    return { x: center.x + r * Math.cos(t), y: center.y + r * Math.sin(t) }
  })
}

/**
 * 구역 다각형 — 멤버 CCTV 볼록 껍질(convex hull)을 margin 만큼 바깥으로
 * 오프셋한 것. 소속·겹침 판정은 여전히 원(center/radius)으로 하되, 화면에
 * 그리는 모양은 "CCTV 를 선으로 이은 다각형" 이라야 이웃 구역과 덜 겹친다
 * (원은 길쭉한 구역에서 위아래 남의 구역을 크게 침범했다).
 * 멤버가 2대 이하(껍질이 안 만들어짐)면 기존 원으로 폴백.
 */
export function hullPoints(members: Pt[], margin: number): Pt[] {
  const hull = convexHull(members)
  if (hull.length < 3) {
    const c = centroid(members)
    const r = Math.max(...members.map(m => dist(m, c))) + Math.max(margin, BOUNDARY_EPSILON)
    return circlePoints(c, r)
  }
  return offsetConvex(hull, Math.max(margin, BOUNDARY_EPSILON))
}

/** monotone chain convex hull (반시계 방향). */
function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y)
  if (pts.length < 3) return pts
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const build = (list: Pt[]) => {
    const out: Pt[] = []
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop()
      out.push(p)
    }
    out.pop()
    return out
  }
  return [...build(pts), ...build([...pts].reverse())]
}

/**
 * 볼록 다각형을 각 변에서 margin 만큼 바깥으로 민 다각형.
 * 변마다 바깥 법선 방향으로 평행이동한 직선들을 이웃끼리 교차시킨다(마이터).
 */
function offsetConvex(hull: Pt[], margin: number): Pt[] {
  const c = centroid(hull)
  const n = hull.length
  // 각 변의 오프셋 직선: 점 p + 방향 d
  const lines = hull.map((a, i) => {
    const b = hull[(i + 1) % n]
    const len = dist(a, b) || 1
    const dx = (b.x - a.x) / len
    const dy = (b.y - a.y) / len
    // 법선 두 방향 중 무게중심에서 멀어지는 쪽이 바깥
    let nx = -dy
    let ny = dx
    if ((a.x + nx - c.x) ** 2 + (a.y + ny - c.y) ** 2 < (a.x - c.x) ** 2 + (a.y - c.y) ** 2) {
      nx = -nx
      ny = -ny
    }
    return { px: a.x + nx * margin, py: a.y + ny * margin, dx, dy }
  })
  const out: Pt[] = []
  for (let i = 0; i < n; i++) {
    const l1 = lines[(i + n - 1) % n]
    const l2 = lines[i]
    const det = l1.dx * l2.dy - l1.dy * l2.dx
    if (Math.abs(det) < 1e-9) {
      // 거의 평행(일직선 꼭짓점) — 그 변 위의 점으로 대신
      out.push({ x: l2.px, y: l2.py })
      continue
    }
    const t = ((l2.px - l1.px) * l2.dy - (l2.py - l1.py) * l2.dx) / det
    out.push({ x: l1.px + l1.dx * t, y: l1.py + l1.dy * t })
  }
  return out
}

/**
 * 기존 CCTV 이름들에서 가장 많이 쓰인 `P{동}_B{층}` 접두어. 규격 이름이 하나도
 * 없으면 null (호출자가 사용자에게 입력받는다).
 */
export function inferNamePrefix(names: string[]): string | null {
  const counts = new Map<string, number>()
  for (const name of names) {
    const m = NAME_PARTS_RE.exec(name.trim())
    if (!m) continue
    counts.set(m[1], (counts.get(m[1]) ?? 0) + 1)
  }
  let best: string | null = null
  let bestCount = 0
  for (const [prefix, count] of counts) {
    if (count > bestCount) {
      best = prefix
      bestCount = count
    }
  }
  return best
}

/** 규격 이름(`P1_B1_2_6`)을 이미 가지고 있는가. */
function hasProperName(camera: PlannerCamera): boolean {
  return CCTV_NAME_RE.test(camera.name.trim())
}

/**
 * 위→아래 행 단위로 훑고 각 행은 좌→우. rowTolerance 는 같은 행으로 볼 y 차이
 * (보통 CCTV 아이콘 높이) — 0 이면 순수 (y, x) 정렬이 된다.
 */
export function scanOrder<T extends Pt>(cameras: T[], rowTolerance: number): T[] {
  const sorted = [...cameras].sort((a, b) => a.y - b.y || a.x - b.x)
  const rows: T[][] = []
  for (const cam of sorted) {
    const row = rows[rows.length - 1]
    // 행의 기준은 첫 멤버의 y — 누적 드리프트로 한 행이 무한히 늘어나는 것을 막는다.
    if (row && Math.abs(cam.y - row[0].y) <= rowTolerance) row.push(cam)
    else rows.push([cam])
  }
  return rows.flatMap(row => [...row].sort((a, b) => a.x - b.x))
}

/**
 * 구역 미배정 카메라를 NVR 용량 한도 안에서 최대로 묶는다.
 *
 * @throws 용량 입력이 0 이하이면 사용자용 Error.
 */
export function planCapacityZones(
  cameras: PlannerCamera[],
  input: CapacityInput,
  opts: { existingZoneNumbers: number[]; margin?: number }
): { zones: PlannedCapacityZone[]; unplaceable: string[] } {
  if (!(input.nvrGb > 0) || !(input.omniGb > 0) || !(input.onewayGb > 0)) {
    throw new Error('용량은 0보다 커야 합니다 (NVR 용량 / 전방위 1대 / 단방향 1대).')
  }

  const margin = opts.margin ?? 0
  const remaining = cameras.filter(c => c.zoneNumber === null)
  const unplaceable: string[] = []
  const zones: PlannedCapacityZone[] = []
  let nextNumber = Math.max(0, ...opts.existingZoneNumbers) + 1

  while (remaining.length > 0) {
    // 가장자리부터 채운다 — 중앙부터 채우면 사방에 자투리가 남는다.
    const center = centroid(remaining)
    let seedIndex = 0
    let seedDist = -1
    remaining.forEach((cam, i) => {
      const d = dist(cam, center)
      if (d > seedDist) {
        seedDist = d
        seedIndex = i
      }
    })
    const seed = remaining[seedIndex]

    if (cameraGb(seed.kind, input) > input.nvrGb) {
      // 1대도 못 담는 용량 — 무한 루프를 막고 보고만 한다.
      unplaceable.push(seed.id)
      remaining.splice(seedIndex, 1)
      continue
    }

    // 이 구역에 담을 최대 대수: 채널 상한과, 구역 수를 지정했으면 남은 카메라를
    // 남은 구역 수로 균등 분배한 몫(올림) 중 작은 쪽. 용량은 아래에서 따로 본다.
    const zonesLeft =
      input.targetZoneCount && input.targetZoneCount > zones.length
        ? input.targetZoneCount - zones.length
        : null
    const evenShare = zonesLeft ? Math.ceil(remaining.length / zonesLeft) : Infinity
    const maxCount = Math.min(input.maxChannels ?? Infinity, evenShare)

    const byNearest = [...remaining].sort((a, b) => dist(a, seed) - dist(b, seed))
    const members: PlannerCamera[] = []
    let usedGb = 0
    for (const cam of byNearest) {
      const gb = cameraGb(cam.kind, input)
      // 가까운 순서대로만 담고 첫 초과에서 멈춘다. 넘는 카메라를 건너뛰고 더 먼
      // 카메라를 담으면 건너뛴 카메라가 원 안에 남아 소속이 어긋난다.
      if (usedGb + gb > input.nvrGb || members.length >= maxCount) break
      members.push(cam)
      usedGb += gb
    }

    const zoneCenter = centroid(members)
    const radius = Math.max(...members.map(m => dist(m, zoneCenter))) + margin
    zones.push({
      number: nextNumber,
      name: `${nextNumber}구역`,
      center: zoneCenter,
      radius,
      points: hullPoints(members, margin),
      memberIds: members.map(m => m.id),
      usedGb,
    })
    nextNumber += 1

    const taken = new Set(members.map(m => m.id))
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      if (taken.has(remaining[i].id)) remaining.splice(i, 1)
    }
  }

  return { zones, unplaceable }
}

/**
 * 이름 없는(또는 규격 외) 카메라에 `{prefix}_{구역}_{순번}` 을 부여한다.
 * 구역이 없는 카메라는 이름을 만들 수 없으므로 건너뛴다.
 *
 * 순번은 그 구역에 이미 있는 최대 순번 다음부터 이어지고, 맵 전체에서 이미
 * 쓰인 이름과 겹치면 다음 번호로 넘어간다.
 */
export function planCctvNames(
  cameras: PlannerCamera[],
  newZones: PlannedCapacityZone[],
  prefix: string,
  rowTolerance: number
): PlannedCctvName[] {
  if (!prefix) return []

  // 신규 구역 배정을 반영한 카메라별 구역번호
  const zoneOf = new Map<string, number>()
  for (const cam of cameras) {
    if (cam.zoneNumber !== null) zoneOf.set(cam.id, cam.zoneNumber)
  }
  for (const zone of newZones) {
    for (const id of zone.memberIds) zoneOf.set(id, zone.number)
  }

  const used = new Set<string>()
  const maxSeq = new Map<number, number>()
  for (const cam of cameras) {
    const name = cam.name.trim()
    const m = NAME_PARTS_RE.exec(name)
    if (!m) continue
    used.add(name)
    // 순번은 "그 구역 이름을 쓰고 있는" 이름에서만 센다 — 이름혼입 카메라의
    // 구역 세그먼트를 따라가면 엉뚱한 구역의 순번을 이어받는다.
    const zoneSegment = Number(m[2])
    const seq = Number(m[3])
    if (seq > (maxSeq.get(zoneSegment) ?? 0)) maxSeq.set(zoneSegment, seq)
  }

  const byZone = new Map<number, PlannerCamera[]>()
  for (const cam of cameras) {
    if (hasProperName(cam)) continue
    const zoneNumber = zoneOf.get(cam.id)
    if (zoneNumber === undefined) continue
    const list = byZone.get(zoneNumber)
    if (list) list.push(cam)
    else byZone.set(zoneNumber, [cam])
  }

  const names: PlannedCctvName[] = []
  for (const zoneNumber of [...byZone.keys()].sort((a, b) => a - b)) {
    let seq = (maxSeq.get(zoneNumber) ?? 0) + 1
    for (const cam of scanOrder(byZone.get(zoneNumber)!, rowTolerance)) {
      let name = `${prefix}_${zoneNumber}_${seq}`
      while (used.has(name)) {
        seq += 1
        name = `${prefix}_${zoneNumber}_${seq}`
      }
      used.add(name)
      names.push({ id: cam.id, name })
      seq += 1
    }
  }
  return names
}

/**
 * 구역 생성 + 이름 부여를 한 번에 계획한다. 그래프에는 아무 변경도 하지 않고,
 * 호출자가 결과를 보고 셀을 만들고 이름을 적용한다.
 */
export function planZonesAndNames(
  cameras: PlannerCamera[],
  input: CapacityInput,
  opts: {
    existingZoneNumbers: number[]
    /** 원 반지름에 더할 여백 — 보통 CCTV 아이콘 반지름. */
    margin?: number
    /** 순번 부여 시 같은 행으로 볼 y 차이. */
    rowTolerance?: number
    /** 이름 접두어. 빈 값이면 이름은 부여하지 않는다. */
    namePrefix: string
  }
): CapacityPlan {
  const { zones, unplaceable } = planCapacityZones(cameras, input, opts)
  const names = planCctvNames(cameras, zones, opts.namePrefix, opts.rowTolerance ?? 0)

  // 원 안에 있지만 그 구역 소속이 아닌 카메라 — 원 겹침 경고용.
  let strayInsideCount = 0
  for (const zone of zones) {
    const members = new Set(zone.memberIds)
    for (const cam of cameras) {
      if (members.has(cam.id)) continue
      if (dist(cam, zone.center) <= zone.radius) strayInsideCount += 1
    }
  }

  return {
    zones,
    names,
    unplaceable,
    strayInsideCount,
    namePrefix: opts.namePrefix || null,
  }
}
